import { PropsWithChildren } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { resendUserInvite } from 'api/account';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AccountMemberOrInvite } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ResendMemberInviteDialogProps extends PropsWithChildren {
    member: AccountMemberOrInvite;
}

const ResendMemberInviteDialog = (props: ResendMemberInviteDialogProps) => {
    const toast = useToast();
    const { children, member } = props;
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const { mutate: remindInvite, isPending: isRemindingInvite } = useMutation({
        mutationFn: () => resendUserInvite(accountId, member.invite_id),
        onSuccess: () => {
            toast.success('Invite reminder sent successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.membersWithInvitesWithFilters(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to send invite reminder');
        },
    });

    return (
        <AlertDialog.Root type={'info'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Resend invite?</AlertDialog.Title>
                        <AlertDialog.Description>
                            The user will receive an email reminder.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            disabled={isRemindingInvite}
                            onClick={() => remindInvite()}
                        >
                            Resend
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const ResendMemberInviteDialogTrigger = AlertDialog.Trigger;

const Root = ResendMemberInviteDialog;
const Trigger = ResendMemberInviteDialogTrigger;

export type { ResendMemberInviteDialogProps };

export {
    ResendMemberInviteDialog,
    ResendMemberInviteDialogTrigger,
    //
    Root,
    Trigger,
};
